import { forwardRef } from 'react';
import styles from './BunnyVideo.module.css';

/** Librería de Bunny Stream del cliente (`EventoAzrahel`). No es un secreto: va
 *  en la URL del reproductor, que es pública por definición. */
export const BUNNY_LIBRARY_ID = '750890';

/** Zona de tiro de la librería. Sirve el vídeo Y su fotograma de portada. */
export const BUNNY_CDN = 'vz-d2c24dc2-44b.b-cdn.net';

export interface BunnyVideoProps {
  /** GUID del vídeo en Bunny Stream. */
  videoId: string;
  /** Qué se ve, para quien no puede verlo. */
  description: string;
  /** Sólo donde HAY un gesto previo del usuario. Ver el comentario de abajo. */
  autoPlay?: boolean;
  className?: string;
  /**
   * MP4 servido por la propia web, como ÚLTIMA fuente. Si Bunny no tiene el vídeo
   * codificado (404), el navegador pasa de `<source>` en `<source>` él solo y
   * acaba aquí; cuando Bunny lo codifique, las de delante ganan y ésta no se pide.
   */
  fallbackSrc?: string;
  /** Carátula local; por defecto la de Bunny. Hace falta junto a `fallbackSrc`. */
  poster?: string;
}

/**
 * EL REPRODUCTOR ES NUESTRO, NO EL IFRAME DE BUNNY, Y ESA ES LA CLAVE.
 *
 * Con el `<iframe>` el vídeo no podía arrancar con sonido, y no por capricho del
 * navegador: la política de autoarranque mira si HAY GESTO DEL USUARIO EN ESE
 * DOCUMENTO, y un iframe de otro origen recién nacido es otro documento que
 * jamás recibió el clic. Con un `<video>` en NUESTRA página, el clic que abre el
 * diálogo ES el gesto. Sin clic previo —un vídeo incrustado en la página— no hay
 * gesto y NINGÚN navegador lo dejará sonar solo: ahí se pinta el fotograma de
 * portada con los controles, que es un estado honesto.
 *
 * CÓMO SE ELIGE LA CALIDAD, sin una sola dependencia:
 *   1. `playlist.m3u8` primero — HLS adaptativo. Safari e iOS lo reproducen
 *      nativo, y son justo los que peor llevan un MP4 gigante.
 *   2. MP4 de respaldo para Chrome/Android, que no leen HLS sin una librería de
 *      40 KB: el navegador descarta la fuente que no sabe tocar y baja a ésta él
 *      solo, sin JS. 480p en móvil (28 MB) y 720p en escritorio (48 MB).
 */
export const BunnyVideo = forwardRef<HTMLVideoElement, BunnyVideoProps>(function BunnyVideo(
  { videoId, description, autoPlay = false, className, fallbackSrc, poster },
  ref,
) {
  const base = `https://${BUNNY_CDN}/${videoId}`;
  /* Se decide UNA vez, al montar: cambiar de fuente a mitad de reproducción
   * reiniciaría el vídeo. Móvil se lleva 480p; 28 MB frente a 48. */
  const mp4 = typeof window !== 'undefined' && window.innerWidth < 900 ? '480p' : '720p';

  return (
    <video
      ref={ref}
      className={[styles.video, className].filter(Boolean).join(' ')}
      poster={poster ?? `${base}/thumbnail.jpg`}
      title={description}
      controls
      autoPlay={autoPlay}
      playsInline
      /* `none`, Y ESTO SE DECIDIÓ MIDIENDO. Con `metadata` el navegador no se
       * quedaba en la cabecera: pedía el manifiesto HLS Y EL PRIMER SEGMENTO DE
       * VÍDEO (`240p/video0.ts`) en cuanto cargaba la página. O sea, todo el que
       * entra paga datos por un vídeo que quizá no va a ver — y en el pase
       * completo esa descarga compitiendo con las fotos del hero tumbó tres
       * pruebas por tiempo de espera. El fotograma de portada es un `poster`,
       * una imagen aparte, así que se sigue viendo igual. */
      preload="none"
    >
      <source src={`${base}/playlist.m3u8`} type="application/vnd.apple.mpegurl" />
      <source src={`${base}/play_${mp4}.mp4`} type="video/mp4" />
      {fallbackSrc ? <source src={fallbackSrc} type="video/mp4" /> : null}
    </video>
  );
});
